import { rage, useObservable } from '~/rage';
import { Gender } from '~/shared';
import {
	setFaceMorphValues,
	setGender,
	setParentsValue,
	useLayoutDispatch,
	useLayoutSelector,
} from '../redux';

const useRandomAppearance = () => {
	const dispatch = useLayoutDispatch();
	const parentConfigValues = useObservable(
		rage.characterCreation.getParentConfigs(),
	);
	const faceMorphConfigs = useObservable(
		rage.characterCreation.getFaceMorphConfigs(),
	);
	const overlayValues = useLayoutSelector(
		(state) => state.characterCreation.overlayValues,
	);
	const gender = useLayoutSelector((state) => state.characterCreation.gender);

	const generateParents = (gender: Gender): [number, number] => {
		const availableParents = parentConfigValues.filter(
			(person) => person.gender === gender,
		);
		const firstParentIndex = Math.floor(
			Math.random() * availableParents.length,
		);
		const firstParent = availableParents[firstParentIndex];

		const remainingParents = availableParents.filter(
			(p) => p.index !== firstParent.index,
		);

		const secondParentIndex = Math.floor(
			Math.random() * remainingParents.length,
		);
		const secondParent = remainingParents[secondParentIndex];

		return [firstParent.index, secondParent.index];
	};

	const getRandomFloat = (
		min: number,
		max: number,
		decimals: number = 1,
	): number => {
		const str = (Math.random() * (max - min) + min).toFixed(decimals);
		return parseFloat(str);
	};

	const handleRandomAppearance = async (
		randomDNA: boolean,
		randomSimilarity: boolean,
		randomAppearance: boolean,
		newGender?: Gender,
	) => {
		if (newGender == null) {
			newGender = gender;
		}

		if (randomDNA) {
			dispatch(setGender(newGender));
			await rage.characterCreation.setGenderValue(newGender);

			const [parent1, parent2] = generateParents(newGender);

			const newParentsValue = {
				momIndex: parent1,
				dadIndex: parent2,
				faceBlend: randomSimilarity ? getRandomFloat(-1, 1, 2) : 0,
				skinBlend: randomSimilarity ? getRandomFloat(-1, 1, 2) : 0,
			};

			dispatch(setParentsValue(newParentsValue));
			await rage.characterCreation.setParentsValue(newParentsValue);
		}

		if (randomAppearance) {
			const newFaceMorphValues = faceMorphConfigs.map((item) => ({
				index: item.index,
				value: getRandomFloat(-1, 1, 2),
			}));

			dispatch(setFaceMorphValues(newFaceMorphValues));
			await rage.characterCreation.setFaceMorphValues(newFaceMorphValues);
			await rage.characterCreation.setOverlayValues(overlayValues);
		}

		return gender;
	};

	return handleRandomAppearance;
};

export { useRandomAppearance };
